// Format duration in a human-readable way (e.g., "1h 36m 56s")
export function formatDuration(startTime, endTime = null) {
	const end = endTime || Date.now();
	const durationMs = end - startTime;
	
	if (durationMs < 0) return '0s';
	
	const totalSeconds = Math.floor(durationMs / 1000);
	const days = Math.floor(totalSeconds / (24 * 60 * 60));
	const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
	const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
	const seconds = totalSeconds % 60;
	
	const parts = [];
	if (days > 0) parts.push(`${days}d`);
	if (hours > 0) parts.push(`${hours}h`);
	if (minutes > 0) parts.push(`${minutes}m`);
	if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
	
	return parts.join(' ');
}

// Format timestamp for display (compact format)
export function formatTimestamp(ts) {
	const date = new Date(ts);
	const now = new Date();
	const isToday = date.toDateString() === now.toDateString();
	const isThisYear = date.getFullYear() === now.getFullYear();
	
	if (isToday) {
		// Today: just show time
		return date.toLocaleString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		});
	} else if (isThisYear) {
		// This year: show month, day, and time
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		});
	} else {
		// Other year: show month, day, year, and time
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		});
	}
}

// Calculate all incidents (both active and resolved) from history
export function calculateIncidents(monitors = [], histories = {}) {
	const incidents = [];
	
	for (const monitor of monitors) {
		const rows = histories[monitor.id] || [];
		if (rows.length === 0) continue;
		
		// Rows are ordered most recent first
		// We need to find transitions: up->down (start) and down->up (end)
		// Iterate from most recent to oldest
		
		let currentIncident = null;
		let previousStatus = null;
		let loopStartIndex = 1; // Default start index for the loop
		
		// Check if we start with a down status (ongoing incident)
		const latest = rows[0];
		if (latest && !latest.ok) {
			// Currently down - start tracking an incident
			currentIncident = {
				monitorId: monitor.id,
				monitorName: monitor.name,
				monitorUrl: monitor.url,
				intervalMinutes: monitor.intervalMinutes,
				startTime: latest.ts,
				endTime: null,
				rootCause: latest.status || 'T/O',
				rootCauseType: latest.status ? 'HTTP' : 'TIMEOUT',
				recovered: false,
				historyRows: [latest]
			};
			previousStatus = false; // down
		} else {
			// Currently up - check if there was a recovery transition
			// If rows[1] exists and is down, that means we just recovered
			if (rows.length > 1 && rows[1] && !rows[1].ok) {
				// We just recovered - create a recovered incident
				// We'll need to find when the outage started by looking further back
				let incidentStartTime = rows[1].ts;
				let incidentRootCause = rows[1].status || 'T/O';
				let incidentHistoryRows = [rows[1]];
				
				// Look backwards to find when the outage started
				for (let i = 2; i < rows.length; i++) {
					const row = rows[i];
					if (!row || typeof row.ts !== 'number') break;
					if (row.ok) break; // Found the transition point
					incidentStartTime = row.ts;
					incidentHistoryRows.push(row);
					if (row.status) {
						incidentRootCause = row.status;
					}
				}
				
				// Create the recovered incident
				incidents.push({
					monitorId: monitor.id,
					monitorName: monitor.name,
					monitorUrl: monitor.url,
					intervalMinutes: monitor.intervalMinutes,
					startTime: incidentStartTime,
					endTime: latest.ts, // Recovery time is when it came back up
					rootCause: incidentRootCause,
					rootCauseType: incidentRootCause === 'T/O' ? 'TIMEOUT' : 'HTTP',
					recovered: true,
					historyRows: incidentHistoryRows
				});
				
				// Skip the rows we've already processed in the recovered incident
				// Find the first up entry before the outage to set previousStatus correctly
				// and determine where to start the loop
				for (let i = 2; i < rows.length; i++) {
					const row = rows[i];
					if (!row || typeof row.ts !== 'number') break;
					if (row.ok) {
						// Found the up entry before the outage - start loop from here
						loopStartIndex = i;
						previousStatus = true; // up
						break;
					}
				}
				// If we didn't find an up entry before the outage, the outage goes all the way back
				// In this case, we've already processed everything, so we can skip the loop
				if (previousStatus === null) {
					loopStartIndex = rows.length; // Skip the entire loop
					previousStatus = true; // Set to up since we're done processing
				}
			} else {
				previousStatus = true; // up
			}
		}
		
		for (let i = loopStartIndex; i < rows.length; i++) {
			const row = rows[i];
			if (!row || typeof row.ts !== 'number') continue;
			
			const isDown = !row.ok;
			
			// Detect transition from up to down (start of new incident)
			if (isDown && previousStatus === true) {
				// New incident started
				currentIncident = {
					monitorId: monitor.id,
					monitorName: monitor.name,
					monitorUrl: monitor.url,
					intervalMinutes: monitor.intervalMinutes,
					startTime: row.ts,
					endTime: null,
					rootCause: row.status || 'T/O',
					rootCauseType: row.status ? 'HTTP' : 'TIMEOUT',
					recovered: false,
					historyRows: [row]
				};
			}
			
			// If we're in an incident, add this row
			if (currentIncident && isDown) {
				currentIncident.historyRows.push(row);
				// Update start time to the oldest down entry
				currentIncident.startTime = row.ts;
				// Update root cause to the most recent error (first in array)
				if (currentIncident.historyRows.length === 1 || row.status) {
					currentIncident.rootCause = row.status || 'T/O';
					currentIncident.rootCauseType = row.status ? 'HTTP' : 'TIMEOUT';
				}
			}
			
			// Detect transition from down to up (end of incident)
			if (!isDown && previousStatus === false && currentIncident) {
				// Incident ended - use the previous row's timestamp (when it was still down)
				// The current row is when it came back up
				if (i > 0 && rows[i - 1]) {
					currentIncident.endTime = row.ts; // When it came back up
				}
				currentIncident.recovered = true;
				incidents.push(currentIncident);
				currentIncident = null;
			}
			
			previousStatus = !isDown; // true = up, false = down
		}
		
		// If there's an ongoing incident (still down), add it
		if (currentIncident) {
			incidents.push(currentIncident);
		}
	}
	
	// Sort: ongoing incidents first, then by start time (most recent first)
	return incidents.sort((a, b) => {
		// Prioritize ongoing incidents
		if (a.recovered !== b.recovered) {
			return a.recovered ? 1 : -1; // Ongoing (recovered=false) comes first
		}
		// Within same status, sort by start time (most recent first)
		return b.startTime - a.startTime;
	});
}


